import type { OrganizationWeeklyLeadersEmail } from '@whizz/api-model';
import type { PreviewData } from './modules/OrganizationEmailPreview';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { repository } from '@whizz/api-client';
import AdminDashboardLayout from '../../../layouts/AdminDashboardLayout';
import Button from '../../../components/button/Button';
import { MaterialIcon } from '../../../components';
import { OrganizationEmailConfigurationForm } from './modules/OrganizationEmailConfigurationForm';
import OrganizationEmailPreview from './modules/OrganizationEmailPreview';
import { ADMIN_ROUTES, QUERIES_IDS, ROUTES } from '../../../util/vars';
import NoData from '../../../global/sections/NoData';
import { useI18n } from '@whizz/react-i18n';

const OrganizationWeeklyLeadersEmailsPageComponent = () => {
  const { translate: t } = useI18n();
  const { organizationId } = useParams<{ organizationId: string }>();
  const history = useHistory();

  const getOrgWeeklyLeadersEmail = useQuery({
    queryKey: [ QUERIES_IDS.ADMIN.GET_ORGANISATION_WEEKLY_LEADERS_EMAIL, organizationId ],
    queryFn: () => repository.AdminRepository.getOrganizationWeeklyLeadersEmail(Number(organizationId)),
  });

  const state = getOrgWeeklyLeadersEmail.isLoading ? 'loading' : (getOrgWeeklyLeadersEmail.data ? 'loaded' : 'error');
  const email = getOrgWeeklyLeadersEmail.data?.data ?? null;

  const backToOrganizations = () => history.push(`${ROUTES.superAdmin}${ADMIN_ROUTES.organizations}`);

  return (
    <AdminDashboardLayout
      title={t('pages.ADMIN.HEADER')}
      action={
        <Button
          type={'button'}
          isOutline
          onClick={backToOrganizations}
        >
          <MaterialIcon icon={'ChevronLeft'} />
          {t('pages.ADMIN.ORGANIZATIONS.CHANGE')}
        </Button>
      }
    >
      {state === 'loading' && (
        <NoData noDataMessage={t('pages.ADMIN.LEADERS_EMAILS.LOADING')} />
      )}
      {email && (
        <View email={email} />
      )}
    </AdminDashboardLayout>
  );
};

const View = ({ email }: { email: OrganizationWeeklyLeadersEmail }) => {
  const { translate: t } = useI18n();
  const [ previewData, setPreviewData ] = useState<PreviewData>({
    isEnabled: email.isEnabled ?? false,
    organization: email.organization,
    startDate: null,
    endDate: null,
    contacts: email.contacts.map((c) => c.firstname),
    standardGreeting: email.standardGreeting ?? true,
    message: null,
    reminderMessage: null,
    metricsComponents: email.metricsComponents ?? [],
  });

  return (
    <div className={'flex gap-5'}>
      <div className={'flex flex-col gap-5 w-1/4 p-md rounded-lg bg-white shadow'}>
        <h2 className={'text-2xl font-bold'}>{email.organization.name}</h2>
        <span className={'text-sm text-gray-500'}>{email.organization.country ? t(`countries.COUNTRY_${email.organization.country}`) : ''}</span>
        <hr className={'h-px my-4 bg-gray-300 border-0'} />
        <div className={'flex flex-col gap-2'}>
          <h3 className={'font-bold'}>{t('pages.ADMIN.ORGANIZATIONS.MEMBER_SCHOOLS', { count: email.schools.length })}</h3>
          <ul className={'text-sm space-y-1 ml-4'}>
            {email.schools.map((school) => (
              <li key={school.id}>{school.name}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className={'flex flex-col gap-5 w-3/4'}>
        <div className={'grid grid-cols-2 gap-10'}>
          <OrganizationEmailConfigurationForm
            email={email}
            setPreviewData={setPreviewData}
          />
          <OrganizationEmailPreview
            previewData={previewData}
          />
        </div>
      </div>
    </div>
  );
};

export { OrganizationWeeklyLeadersEmailsPageComponent };
